import { supabase } from "../utils/supabaseClient";
import { Zone } from "../types/zone";

interface ZonePayload {
    branch_id: string;
    id?: string;
    zone_name?: string;
    ar_zone_name?: string;
    fee?: number;
    deleted_id?: string;
}

interface ZoneApiResponse {
    success: boolean;
    message: string;
    data?: Zone[] | { id: string };
}

/**
 * Fetch all zones for a branch
 */
export const getBranchZones = async (branchId: string): Promise<Zone[]> => {
    const { data, error } = await supabase.rpc("get_branch_zones" as any, {
        payload: {
            branch_id: branchId
        }
    });

    if (error) throw error;

    const response = data as unknown as ZoneApiResponse;
    if (!response || response.success === false) {
        return [];
    }

    return (response.data as Zone[]) || [];
};

/**
 * Create a new zone
 */
export const createZone = async (
    branchId: string,
    zoneData: { zone_name: string; ar_zone_name: string; fee: number }
): Promise<Zone> => {
    const payload: ZonePayload = {
        branch_id: branchId,
        zone_name: zoneData.zone_name,
        ar_zone_name: zoneData.ar_zone_name,
        fee: zoneData.fee
    };

    const { data, error } = await supabase.rpc("upsert_branch_zone" as any, {
        payload
    });

    if (error) throw error;

    const response = data as unknown as ZoneApiResponse;
    if (!response || response.success === false) {
        throw new Error(response?.message || "Failed to create zone");
    }

    const resultData = response.data as { id: string };
    return {
        id: resultData.id,
        zone_name: zoneData.zone_name,
        ar_zone_name: zoneData.ar_zone_name,
        fee: zoneData.fee,
        branch_id: branchId
    };
};

/**
 * Update an existing zone
 */
export const updateZone = async (
    branchId: string,
    zoneId: string,
    zoneData: Partial<{ zone_name: string; ar_zone_name: string; fee: number }>
): Promise<Zone> => {
    const payload: ZonePayload = {
        branch_id: branchId,
        id: zoneId,
        ...zoneData
    };

    const { data, error } = await supabase.rpc("upsert_branch_zone" as any, {
        payload
    });

    if (error) throw error;

    const response = data as unknown as ZoneApiResponse;
    if (!response || response.success === false) {
        throw new Error(response?.message || "Failed to update zone");
    }

    return {
        id: zoneId,
        zone_name: zoneData.zone_name || "",
        ar_zone_name: zoneData.ar_zone_name || "",
        fee: zoneData.fee || 0,
        branch_id: branchId
    };
};

/**
 * Delete a zone
 */
export const deleteZone = async (branchId: string, zoneId: string): Promise<void> => {
    const payload: ZonePayload = {
        branch_id: branchId,
        deleted_id: zoneId
    };

    const { data, error } = await supabase.rpc("upsert_branch_zone" as any, {
        payload
    });

    if (error) throw error;

    const response = data as unknown as ZoneApiResponse;
    if (!response || response.success === false) {
        throw new Error(response?.message || "Failed to delete zone");
    }
};
